
import { VNodeChildren } from 'vue';
import {
  PropType,
  defineComponent,
  h,
  inject,
  ref
} from '@vue/composition-api';

import {
  IFieldErrors,
  IAccessPredicateNode,
  AccessPredicateNodeBinaryOperators,
  AccessPredicateNodeOperandSubtype,
  AccessPredicateNodeOperandTypeInfo,
  AccessPredicateNode,
  AccessPredicateNodeOperatorInfo,
  IWorkflowFormField,
  IWorkflowFormSectionField
} from '@/models';
import { PredicateNodeType } from '@/models/predicate/predicate.constant';
import { ExpressionPredicateNodeTree } from '@/models/predicate/tree';

import { requiredValidator } from '@/utils/validators';

import { useProxiedModel } from '@/composable/proxymodel';
import WorkflowFormBuilderFieldMetaLabelAutoPicker from '../computation/WorkflowFormBuilderFieldMetaLabelAutoPicker.vue';
import { SelectedWorkflowFormFieldKey } from '../tabs/WorkflowFormBuilderCalculationTab.vue';
import { createNamespacedHelpers, useStore } from 'vuex-composition-helpers';
import { IRootState } from '@/store';
import {
  IWorkflowFormTemplateRevisionGetters,
  IWorkflowFormTemplateState
} from '@/store/workflow-form-template';

export default defineComponent({
  name: 'AccessPredicatePredicateQueryTree',

  props: {
    value: {
      type: Array as PropType<
        ExpressionPredicateNodeTree<IAccessPredicateNode>
      >,
      default: () => []
    },
    fieldErrors: {
      type: Array as PropType<IFieldErrors[]>,
      default: (): IFieldErrors[] => []
    },
    disabled: {
      type: Boolean,
      default: false
    },
    level: {
      type: Number,
      default: 0
    }
  },

  emits: ['input'],

  setup(props, { emit }) {
    const formFieldContext = inject(SelectedWorkflowFormFieldKey);

    if (!formFieldContext) {
      throw Error('No context provided for SelectedWorkflowFormFieldKey');
    }

    const store = useStore<IRootState>();

    const { useGetters } = createNamespacedHelpers<
      IWorkflowFormTemplateState,
      IWorkflowFormTemplateRevisionGetters
    >(store, 'workflowFormTemplate');

    const {
      getWorkflowFormFieldsOptionsByTemplateIdAndFieldId,
      getWorkflowFormTemplateRevisionById
    } = useGetters([
      'getWorkflowFormFieldsOptionsByTemplateIdAndFieldId',
      'getWorkflowFormTemplateRevisionById'
    ]);

    const nodes = useProxiedModel(props, 'value');

    /**
     * Get field ID from meta_label by looking it up in the revision's sections
     */
    function getFieldIdFromMetaLabel(
      metaLabel: string | null | undefined
    ): number | null {
      if (!metaLabel) {
        return null;
      }

      const formTemplateRevision = getWorkflowFormTemplateRevisionById.value(
        formFieldContext!.revisionId
      );

      if (!formTemplateRevision || !formTemplateRevision.sections) {
        return null;
      }

      for (
        let sectionIndex = 0;
        sectionIndex < formTemplateRevision.sections.length;
        sectionIndex++
      ) {
        const sectionFields =
          formTemplateRevision.sections[sectionIndex].section_fields;

        for (
          let sectionFieldIndex = 0;
          sectionFieldIndex < sectionFields.length;
          sectionFieldIndex++
        ) {
          const sectionField = sectionFields[sectionFieldIndex];
          const fieldTemplate = sectionField.field_template as IWorkflowFormField;

          if (fieldTemplate.meta_label === metaLabel && fieldTemplate.id) {
            return fieldTemplate.id;
          }
        }
      }

      return null;
    }

    function logicalOperatorStringRepresentation(
      subtype: AccessPredicateNodeBinaryOperators
    ) {
      switch (subtype) {
        case AccessPredicateNodeBinaryOperators.And:
          return 'And';
        case AccessPredicateNodeBinaryOperators.Or:
          return 'Or';
        default:
          return 'Unknown';
      }
    }

    function setNodeProperty<T = IAccessPredicateNode>(
      node: T,
      field: keyof T,
      value: T[keyof T]
    ) {
      node[field] = value;

      setNodesValue(nodes.value);
    }

    function setNodesValue(
      nodes: ExpressionPredicateNodeTree<IAccessPredicateNode>
    ) {
      emit('input', nodes);
    }

    function insertConditionExpression(
      tree: ExpressionPredicateNodeTree<IAccessPredicateNode>,
      root: boolean
    ) {
      const StartBracketNode = new AccessPredicateNode({
        type: PredicateNodeType.Operator,
        subtype: AccessPredicateNodeBinaryOperators.StartBracket
      });
      const VariableNode = new AccessPredicateNode({
        type: PredicateNodeType.Operand,
        subtype: AccessPredicateNodeOperandSubtype.Variable,
        variable_name: ''
      });
      const EqualsNode = new AccessPredicateNode({
        type: PredicateNodeType.Operator,
        subtype: AccessPredicateNodeBinaryOperators.Equals
      });
      const ConstantNode = new AccessPredicateNode({
        type: PredicateNodeType.Operand,
        subtype: AccessPredicateNodeOperandSubtype.Constant,
        constant: null
      });
      const CloseBracketNode = new AccessPredicateNode({
        type: PredicateNodeType.Operator,
        subtype: AccessPredicateNodeBinaryOperators.CloseBracket
      });

      const operatorNode = new AccessPredicateNode({
        type: PredicateNodeType.Operator,
        subtype: root
          ? AccessPredicateNodeBinaryOperators.Or
          : AccessPredicateNodeBinaryOperators.And
      });

      const nodeExpressionList = [
        StartBracketNode,
        VariableNode,
        EqualsNode,
        ConstantNode,
        CloseBracketNode
      ];

      if (tree.length > 0) {
        if (root) {
          tree.push(operatorNode);
        } else {
          tree.splice(tree.length - 1, 0, operatorNode);
        }
      }

      if (root) {
        tree.push([StartBracketNode, nodeExpressionList, CloseBracketNode]);
      } else {
        // add expression before the last node (before the closing bracket)
        tree.splice(tree.length - 1, 0, nodeExpressionList);
      }
    }

    function removeSimpleExpressionNodeGroup(
      tree: ExpressionPredicateNodeTree<IAccessPredicateNode>,
      groupIndex: number
    ) {
      let nodesList = [...tree];

      /** Current Node to render */
      const currentGroupExpression = tree[groupIndex];

      /** Previous Node to delete */
      const previousLogicalOperatorNode = tree[groupIndex - 1];

      /** Next Node to delete */
      const nextLogicalOperatorNode = tree[groupIndex + 1];

      if (currentGroupExpression && Array.isArray(currentGroupExpression)) {
        // Remove the group expression if exists
        nodesList.splice(groupIndex, 1);
      }

      if (
        previousLogicalOperatorNode &&
        !Array.isArray(previousLogicalOperatorNode) &&
        previousLogicalOperatorNode.isOperator()
      ) {
        // Remove the logical operator before the group if it exists
        nodesList.splice(groupIndex - 1, 1);
      } else if (
        nextLogicalOperatorNode &&
        !Array.isArray(nextLogicalOperatorNode) &&
        nextLogicalOperatorNode.isOperator()
      ) {
        // If there is no previous logical operator node, it means this is the first group
        // Check if there is a next logical operator node to remove

        // Remove the logical operator after the group if it exists
        nodesList.splice(groupIndex, 1);
      }

      // Hack because use v-model update is not working
      setNodesValue(nodesList);
    }

    function genNodeBranch() {
      let renderedNodes: VNodeChildren = [];

      let writeIndex = 0;

      for (let readIndex = 0; readIndex < nodes.value.length; readIndex++) {
        if (writeIndex == readIndex) {
          /** Current Node to render */
          const currentNode = nodes.value[readIndex];

          /** Next Node to render */
          const nextNode = nodes.value[readIndex + 1];

          /** After Next Node to render */
          const afterNextNode = nodes.value[readIndex + 2];

          // If currentNode in tree is another expression then rerender this component here
          if (Array.isArray(currentNode)) {
            // Handle nested group by recursion return entire component not just this minor tree

            const newGroup = h('v-row', { props: { dense: true } }, [
              h(
                'v-col',
                {
                  props: { cols: 12 }
                },
                [
                  h('div', { class: 'd-flex flex-row' }, [
                    h(
                      'v-card',
                      {
                        style: {
                          order: 1
                        },
                        class: 'pa-3 w-full',
                        props: {
                          flat: props.level == 0,
                          outlined: props.level == 0,
                          elevation: 1
                        }
                      },
                      [
                        h('AccessPredicatePredicateQueryTree', {
                          props: {
                            value: currentNode,
                            disabled: props.disabled,
                            level: props.level + 1,
                            fieldErrors: props.fieldErrors
                          },
                          on: {
                            input: (
                              value: ExpressionPredicateNodeTree<
                                IAccessPredicateNode
                              >
                            ) => {
                              nodes.value[readIndex] = value;

                              setNodesValue(nodes.value);
                            }
                          }
                        }),

                        props.level == 0 &&
                          h(
                            'v-col',
                            {
                              class: 'pb-0 px-0',
                              props: {
                                cols: 12,
                                align: 'center',
                                justify: 'center'
                              }
                            },
                            [
                              h(
                                'v-tooltip',
                                {
                                  props: {
                                    bottom: true
                                  },
                                  scopedSlots: {
                                    activator: ({ on, attrs }: any) =>
                                      h(
                                        'v-btn',
                                        {
                                          class:
                                            'v-btn--app text-capitalize border-sm',
                                          props: {
                                            color: 'primary',
                                            outlined: true,
                                            small: true,
                                            disabled: props.disabled
                                          },
                                          on: {
                                            ...on,
                                            click: () =>
                                              insertConditionExpression(
                                                currentNode,
                                                false
                                              )
                                          },
                                          attrs
                                        },
                                        [
                                          h(
                                            'v-icon',
                                            {
                                              props: {
                                                left: true
                                              }
                                            },
                                            ['mdi-plus-circle']
                                          ),
                                          'Add Condition'
                                        ]
                                      )
                                  }
                                },
                                'Add Condition Group'
                              )
                            ]
                          )
                      ]
                    ),

                    // Remove group button
                    props.level === 0
                      ? h('v-tooltip', {
                          props: {
                            bottom: true
                          },
                          scopedSlots: {
                            activator: ({ on, attrs }) =>
                              h(
                                'v-btn',
                                {
                                  style: { right: '0px', order: 2 },
                                  class:
                                    'align-self-center justify-self-center ml-2',
                                  props: {
                                    icon: true,
                                    color: 'error',
                                    disabled: props.disabled
                                  },
                                  on: {
                                    ...on,
                                    click: () =>
                                      removeSimpleExpressionNodeGroup(
                                        nodes.value,
                                        readIndex
                                      )
                                  },
                                  attrs
                                },
                                [h('v-icon', ['mdi-minus-circle'])]
                              ),
                            default: () => {
                              return 'Remove Group';
                            }
                          }
                        })
                      : null
                  ])
                ]
              )
            ]);

            renderedNodes.push(newGroup);
            // Move forward with current list to render nodes
            writeIndex++;
          } else if (!currentNode.isCloseBracket()) {
            if (currentNode.isStartBracket()) {
              // if currentNode is a start bracket then continue start new group
              writeIndex++;
            } else if (currentNode.isOperator()) {
              // Handle logical operator
              renderedNodes.push(
                h(
                  'v-col',
                  {
                    props: { cols: 12, align: 'center', justify: 'center' },
                    class: { 'py-0': props.level > 0 }
                  },
                  [
                    props.level == 0 &&
                      h(
                        'div',
                        {
                          class:
                            'd-flex flex-row align-center justify-center position-relative'
                        },
                        [
                          h(
                            'v-btn-toggle',
                            {
                              props: {
                                value: currentNode.subtype,
                                rounded: true,
                                mandatory: true,
                                disabled: props.disabled,
                                dense: true,
                                outlined: true,
                                small: true,
                                backgroundColor:
                                  props.level > 0
                                    ? 'grey darken-2'
                                    : 'background',
                                dark: true
                              },
                              on: {
                                change: (
                                  value: AccessPredicateNodeBinaryOperators
                                ) => {
                                  setNodeProperty(
                                    currentNode,
                                    'subtype',
                                    value
                                  );
                                }
                              }
                            },
                            [
                              h(
                                'v-btn',
                                {
                                  props: {
                                    value:
                                      AccessPredicateNodeBinaryOperators.And,
                                    color:
                                      currentNode.subtype ==
                                      AccessPredicateNodeBinaryOperators.And
                                        ? 'primary'
                                        : '',
                                    outlined:
                                      currentNode.subtype !=
                                      AccessPredicateNodeBinaryOperators.And,
                                    small: true,
                                    disabled: props.level > 0 || props.disabled,
                                    light:
                                      currentNode.subtype !=
                                      AccessPredicateNodeBinaryOperators.And
                                  }
                                },
                                [
                                  logicalOperatorStringRepresentation(
                                    AccessPredicateNodeBinaryOperators.And
                                  )
                                ]
                              ),
                              props.level == 0
                                ? h(
                                    'v-btn',
                                    {
                                      props: {
                                        value:
                                          AccessPredicateNodeBinaryOperators.Or,
                                        color:
                                          currentNode.subtype ==
                                          AccessPredicateNodeBinaryOperators.Or
                                            ? 'primary'
                                            : '',
                                        outlined:
                                          currentNode.subtype !=
                                          AccessPredicateNodeBinaryOperators.Or,
                                        small: true,
                                        disabled: props.disabled,
                                        light:
                                          currentNode.subtype !=
                                          AccessPredicateNodeBinaryOperators.Or
                                      }
                                    },
                                    [
                                      logicalOperatorStringRepresentation(
                                        AccessPredicateNodeBinaryOperators.Or
                                      )
                                    ]
                                  )
                                : null
                            ]
                          )
                        ]
                      )
                  ]
                )
              );
              writeIndex++;
            }
            // CHeck that the next next node is not a new group or an operator
            else if (!Array.isArray(nextNode) && nextNode.isOperator()) {
              // determine row and create group from here

              const currentOperandNodeSubtypeLabel =
                currentNode.subtype ==
                AccessPredicateNodeOperandSubtype.Constant
                  ? 'Constant'
                  : 'Property Name';

              const currentNodeOperand = h(
                WorkflowFormBuilderFieldMetaLabelAutoPicker,
                {
                  style: 'grid-column: span 1',
                  props: {
                    excludeFieldWithTemplate: true,
                    value:
                      currentNode.subtype ==
                      AccessPredicateNodeOperandSubtype.Constant
                        ? currentNode.constant
                        : currentNode.variable_name
                  },
                  attrs: {
                    label: currentOperandNodeSubtypeLabel,
                    outlined: true,
                    dense: true,
                    hideDetails: 'auto',
                    hint:
                      'This field should match the meta_label of the form field',
                    rules: [requiredValidator],

                    errorMessages:
                      (props.fieldErrors[readIndex] &&
                        (currentNode.subtype ==
                        AccessPredicateNodeOperandSubtype.Constant
                          ? props.fieldErrors[readIndex]['constant']
                          : props.fieldErrors[readIndex]['variable_name'])) ||
                      []
                  },
                  on: {
                    input: (value: string) => {
                      setNodeProperty(
                        currentNode,
                        currentNode.subtype ==
                          AccessPredicateNodeOperandSubtype.Constant
                          ? 'constant'
                          : 'variable_name',
                        value
                      );
                    }
                  }
                }
              );

              const nextNodeOperator = h('v-select', {
                style: 'grid-column: span 1',
                props: {
                  label: 'Operator', //nextNode(operator)
                  outlined: true,
                  dense: true,
                  value: nextNode.subtype,
                  items: AccessPredicateNodeOperatorInfo.getAccessPredicateNodeOperatorInfoList(),
                  itemText: 'display_name',
                  itemValue: 'value',
                  rules: [requiredValidator],
                  hideDetails: 'auto',
                  errorMessages:
                    (props.fieldErrors[readIndex + 1] &&
                      props.fieldErrors[readIndex + 1]['subtype']) ||
                    []
                },
                on: {
                  change: (value: AccessPredicateNodeOperandSubtype) =>
                    setNodeProperty(nextNode, 'subtype', value)
                },
                scopedSlots: {
                  selection: ({ item, attrs, disabled }) =>
                    h(
                      'div',
                      {
                        staticClass:
                          'v-select__selection v-select__selection--comma',
                        attrs
                      },
                      [
                        h(
                          'v-icon',
                          {
                            props: {
                              size: 18,
                              color: !disabled ? 'black' : 'text--disabled'
                            }
                          },
                          [item.icon]
                        )
                      ]
                    ),

                  item: ({ item, attrs }) => [
                    h(
                      'v-list-item-action',
                      {
                        class: 'mr-2'
                      },
                      [
                        h(
                          'v-icon',
                          {
                            props: {
                              size: 18,
                              color: !attrs.disabled
                                ? 'black'
                                : 'text--disabled'
                            }
                          },
                          [item.icon]
                        )
                      ]
                    ),

                    h('v-list-item-content', [
                      h('v-list-item-title', [item.display_name])
                    ])
                  ]
                }
              });

              // Get field ID from the current node's variable_name (meta_label)
              // Only needed when subtype is Variable (not Constant)
              const currentFieldId =
                currentNode.subtype ==
                AccessPredicateNodeOperandSubtype.Constant
                  ? null
                  : getFieldIdFromMetaLabel(currentNode.variable_name);

              const afterNodeOperator =
                afterNextNode &&
                !Array.isArray(afterNextNode) &&
                h('v-combobox', {
                  style: 'grid-column: span 1',
                  props: {
                    items: currentFieldId
                      ? getWorkflowFormFieldsOptionsByTemplateIdAndFieldId
                          .value(formFieldContext!.revisionId, currentFieldId)
                          .map(option => option.value)
                      : [],
                    label: 'Value', //afterNextNode(operand - constant)
                    outlined: true,
                    dense: true,
                    value: afterNextNode.constant,
                    rules: [requiredValidator],
                    hideDetails: 'auto',
                    type:
                      currentNode.subtype ==
                      AccessPredicateNodeOperandSubtype.Constant
                        ? 'number'
                        : 'text',

                    errorMessages:
                      (props.fieldErrors[readIndex + 2] &&
                        props.fieldErrors[readIndex + 2]['constant']) ||
                      []
                  },
                  on: {
                    input: (value: AccessPredicateNodeOperandSubtype) =>
                      setNodeProperty(
                        afterNextNode,
                        'constant',
                        currentNode.subtype ==
                          AccessPredicateNodeOperandSubtype.Constant
                          ? Number(value)
                          : value
                      )
                  }
                });

              const currentGroupSubChildren: VNodeChildren = [
                currentNodeOperand,
                nextNodeOperator,
                afterNodeOperator
              ];

              const currentGroupChildren: VNodeChildren = [
                h('v-select', {
                  style: 'grid-column: span 3',
                  on: {
                    change: (value: AccessPredicateNodeOperandSubtype) => {
                      // Update the node before emitting to update the UI as current node is being used in the newly modified group
                      // This is to directly keep the ID of current node so as to not create new nodes unnecessarily
                      setNodeProperty(currentNode, 'subtype', value);
                    }
                  },
                  props: {
                    label: 'Condition Type',
                    outlined: true,
                    dense: true,
                    value: currentNode.subtype,
                    items: AccessPredicateNodeOperandTypeInfo.getAccessPredicateNodeOperandTypeInfoList(),
                    rules: [requiredValidator],
                    itemText: 'display_name',
                    itemValue: 'value',
                    hideDetails: 'auto'
                  }
                }),

                currentGroupSubChildren
              ];

              renderedNodes.push(
                h(
                  'v-col',
                  {
                    class: 'pa-0',
                    props: { cols: 12, align: 'center', justify: 'center' }
                  },
                  [
                    h(
                      'div',
                      {
                        class: 'd-grid gap-8',
                        style: {
                          // Using fixed width to avoid grid jumping when the select changes width
                          // and maintaining consistent width amount of columns to avoid jumping
                          'grid-template-columns': '1fr 75px 1fr',
                          width: '100%'
                        }
                      },
                      currentGroupChildren
                    )
                  ]
                )
              );
              if (afterNextNode) {
                writeIndex += 3;
              } else {
                writeIndex += 2;
              }
            } else {
              writeIndex++;
            }
          }
        }
      }

      return renderedNodes;
    }

    return () =>
      h('div', [
        genNodeBranch(),

        props.level === 0
          ? h('v-row', { props: { dense: true } }, [
              h(
                'v-col',
                {
                  props: {
                    cols: 12
                  }
                },
                [
                  h(
                    'div',
                    { class: 'd-flex align-center justify-space-between' },
                    [
                      h(
                        'v-tooltip',
                        {
                          props: {
                            bottom: true
                          },
                          scopedSlots: {
                            activator: ({ on, attrs }: any) =>
                              h(
                                'v-btn',
                                {
                                  class: 'v-btn--app text-capitalize border-sm',
                                  props: {
                                    color: 'primary',
                                    outlined: true,
                                    small: true,
                                    disabled: props.disabled
                                  },
                                  on: {
                                    ...on,
                                    click: () =>
                                      insertConditionExpression(
                                        nodes.value,
                                        true
                                      )
                                  },
                                  attrs
                                },
                                [
                                  h(
                                    'v-icon',
                                    {
                                      props: {
                                        left: true
                                      }
                                    },
                                    ['mdi-plus-circle']
                                  ),
                                  'Add Condition Group'
                                ]
                              )
                          }
                        },
                        'Add Condition Group'
                      )
                    ]
                  )
                ]
              )
            ])
          : null
      ]);
  }
});
